import { useEffect, useState } from 'react';
import {
  Loader2, Percent, Receipt, TrendingUp, CheckCircle2, FileText, AlertTriangle,
} from 'lucide-react';
import * as api from '../../lib/api';
import CommissionSettings from '../../components/platform/CommissionSettings';
import { formatCents, formatDate } from '../../lib/format';

const STATUS_STYLES = {
  paid: 'bg-emerald-100 text-emerald-800',
  open: 'bg-sky-100 text-sky-800',
  past_due: 'bg-rose-100 text-rose-800',
  void: 'bg-slate-200 text-slate-500',
};

/**
 * The platform's own console.
 *
 * Deliberately its own route tree rather than a tab inside /admin: the person
 * running the platform and the person running the yard are different people
 * with different interests, and the platform admin gets no facility access at
 * all — outranking the owner is not a licence to read a tenant's file.
 */
export default function PlatformHome() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const [settings, setSettings] = useState(null);

  const load = async () => {
    try {
      const [overview, config] = await Promise.all([api.platformOverview(), api.platformSettings()]);
      setSettings(config.settings);
      setData(overview);
    } catch (err) {
      setError(err.message);
      setData({ failed: true });
    }
  };

  useEffect(() => { load(); }, []);

  if (data === null || settings === null) {
    return (
      <div className="grid place-items-center py-20" role="status" aria-label="Loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
      </div>
    );
  }
  if (data.failed) {
    return <p role="alert" className="mx-auto mt-8 max-w-md rounded-lg bg-rose-50 p-4 text-sm text-rose-800">{error}</p>;
  }

  const { current_period: current, invoices } = data;
  const unpaid = invoices.filter((i) => i.status === 'open');

  const issue = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const result = await api.generatePlatformInvoice();
      setNotice(`${result.invoice.invoice_number} issued — ${result.fees_swept} transactions, ${formatCents(result.invoice.total_cents)}.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (invoice) => {
    const note = window.prompt(
      `Mark ${invoice.invoice_number} (${formatCents(invoice.total_cents)}) as paid?\n\n`
      + 'Reference for reconciliation — bank transfer id, cheque number:');
    if (note === null) return;
    setBusy(true); setError(null);
    try {
      await api.markPlatformInvoicePaid(invoice.public_id, note);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Platform</h1>
          <p className="mt-1 text-sm text-slate-600">
            Commission on every successful tenant payment, billed monthly.
          </p>
        </div>
        <button type="button" onClick={issue} disabled={busy || current.transaction_count === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-bold text-white hover:bg-brand-700 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <FileText className="h-4 w-4" aria-hidden="true" />}
          Issue last month
        </button>
      </div>

      {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}
      {notice && (
        <p className="mt-4 flex items-start gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
          {notice}
        </p>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <CommissionSettings settings={settings} onSaved={(next) => { setSettings(next); load(); }} />
        <Stat Icon={TrendingUp} label="Accrued, unbilled" value={formatCents(current.fee_cents)}
              hint={`${current.transaction_count} transaction${current.transaction_count === 1 ? '' : 's'}`} />
        <Stat Icon={Receipt} label="Gross processed" value={formatCents(current.gross_cents)}
              hint="Since the last statement" />
        <Stat Icon={AlertTriangle} label="Awaiting payment"
              value={formatCents(unpaid.reduce((s, i) => s + Number(i.balance_cents), 0))}
              hint={`${unpaid.length} statement${unpaid.length === 1 ? '' : 's'}`}
              tone={unpaid.length ? 'bad' : 'good'} />
      </div>

      <h2 className="mt-8 text-sm font-bold text-slate-900">Statements</h2>
      {invoices.length === 0 ? (
        <div className="mt-3 rounded-xl border border-slate-200 bg-white py-14 text-center">
          <Receipt className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">No statements yet</p>
          <p className="mt-1 text-sm text-slate-500">
            {current.transaction_count > 0
              ? 'Issue one for last month when you are ready.'
              : 'They appear once tenant payments start coming through.'}
          </p>
        </div>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[42rem] text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-[12px] font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Statement</th>
                <th className="px-4 py-2.5">Period</th>
                <th className="px-4 py-2.5 text-right">Transactions</th>
                <th className="px-4 py-2.5 text-right">Due</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((i) => (
                <tr key={i.public_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-bold text-slate-900">{i.invoice_number}</p>
                    <p className="text-xs text-slate-500">
                      on {formatCents(i.gross_cents)} processed
                    </p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDate(i.period_start)}
                    <span className="block text-xs text-slate-400">
                      due {formatDate(i.due_on)}
                      {i.status === 'open' && Number(i.days_overdue) > 0 && (
                        <span className="font-bold text-rose-700"> · {i.days_overdue}d late</span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">{i.transaction_count}</td>
                  <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-900">
                    {formatCents(i.total_cents)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`mr-2 rounded-full px-2 py-0.5 text-[12px] font-bold ${
                      STATUS_STYLES[i.status] ?? STATUS_STYLES.void}`}>
                      {i.status}
                    </span>
                    {i.status === 'open' ? (
                      <button type="button" onClick={() => markPaid(i)} disabled={busy}
                              className="text-xs font-bold text-brand-700 hover:underline disabled:opacity-50">
                        Mark paid
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400" title={i.payment_note ?? ''}>
                        {i.paid_at ? formatDate(String(i.paid_at).slice(0, 10)) : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-slate-500">
        The rate on a fee is the rate in force when the payment landed — changing it here never
        rewrites a statement already issued.
      </p>
    </div>
  );
}

function Stat({ Icon, label, value, hint, tone }) {
  const valueTone = tone === 'bad' ? 'text-rose-700' : 'text-slate-900';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <Icon className="h-5 w-5 text-brand-600" aria-hidden="true" />
      <p className={`mt-3 text-2xl font-extrabold tabular-nums ${valueTone}`}>{value}</p>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-[12px] text-slate-400">{hint}</p>
    </div>
  );
}
